import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  AuthContextType,
  User,
  NewTenantRegistrationData,
  Language,
  AccessibleFeature,
  LoginResult,
} from '../types/auth'
import { MfaLoginResponse } from '../services/authService'
import { authService } from '../services/authService'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// 言語コードのマッピング（バックエンドのLanguage enum -> i18next言語コード）
const mapLanguageToI18n = (lang: Language | undefined): string => {
  if (!lang) return 'ja'
  return lang === 'EN' ? 'en' : 'ja'
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [accessibleFeatures, setAccessibleFeatures] = useState<AccessibleFeature[]>([])

  // アクセス可能な機能を取得
  const fetchAccessibleFeatures = useCallback(async (userId: string) => {
    try {
      const features = await authService.getAccessibleFeatures(userId)
      setAccessibleFeatures(features)
      console.log('🔐 アクセス可能機能を取得:', features.length, '件')
    } catch (error) {
      console.error('アクセス可能機能の取得に失敗:', error)
      setAccessibleFeatures([])
    }
  }, [])

  // 特定のルートにアクセス可能かどうかをチェック
  const canAccessRoute = useCallback(
    (route: string): boolean => {
      // 未認証の場合はfalse
      if (!user) return false

      // ダッシュボードと理解度テストは常にアクセス可能（基本機能）
      if (route === '/dashboard' || route === '/my-enrollments') return true

      // プロフィール関連は常にアクセス可能
      if (route === '/profile' || route === '/change-password') return true

      // テスト受講ページは常にアクセス可能
      if (route.startsWith('/test/')) return true

      // 管理者共通ルート（CORPORATE_ADMIN, SUPER_ADMIN）は常にアクセス可能
      const adminCommonRoutes = ['/question-reports']
      if (
        adminCommonRoutes.includes(route) &&
        (user.role === 'CORPORATE_ADMIN' || user.role === 'SUPER_ADMIN')
      ) {
        return true
      }

      // SUPER_ADMIN専用ルートはSUPER_ADMINなら常にアクセス可能
      const superAdminOnlyRoutes = [
        '/tenant-management',
        '/role-features',
        '/billing',
        '/email-delivery',
        '/email-templates',
        '/tenant-reminder-manage',
        '/announcements',
      ]
      if (superAdminOnlyRoutes.includes(route) && user.role === 'SUPER_ADMIN') {
        return true
      }

      // スケジュール詳細ページはスケジュール管理権限で判定
      if (route.startsWith('/test-schedules/')) {
        return accessibleFeatures.some((feature) => feature.route === '/test-schedules')
      }

      // 学習計画詳細ページは学習計画権限で判定
      if (route.startsWith('/learning-plans/')) {
        return accessibleFeatures.some((feature) => feature.route === '/learning-plans')
      }

      // アクセス可能な機能に含まれているかチェック
      return accessibleFeatures.some((feature) => feature.route === route)
    },
    [user, accessibleFeatures]
  )

  // アクセス可能な機能を再取得（userIdを直接渡すことも可能）
  const refreshAccessibleFeatures = useCallback(
    async (userId?: string) => {
      const targetUserId = userId || user?.id
      if (targetUserId) {
        await fetchAccessibleFeatures(targetUserId)
      }
    },
    [user?.id, fetchAccessibleFeatures]
  )

  // 初期化時にローカルストレージから認証情報を復元
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 AuthContext 初期化開始')
      setIsLoading(true)

      const storedToken = authService.getStoredToken()
      const storedUser = authService.getStoredUser()

      console.log('🔑 認証初期化:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        userEmail: storedUser?.email,
        userName: storedUser?.name,
        storedToken: storedToken,
        rawStoredUser: storedUser,
      })

      if (storedToken && storedUser) {
        try {
          // APIサービスにトークンを設定
          authService.setToken(storedToken)
          // JWTトークンの有効性を確認
          console.log('🔍 JWT token verification...')
          const result = await authService.verifyToken()
          if (result.valid) {
            console.log('✅ JWT token valid')
            console.log('🔍 Setting user:', result.user)
            setToken(storedToken)
            setUser(result.user)
            // アクセス可能な機能を取得
            await fetchAccessibleFeatures(result.user.id)
          } else {
            console.log('JWT token invalid')
            // 無効なトークンの場合はクリア
            await authService.logout()
          }
        } catch (error) {
          console.error('Token verification failed:', error)
          await authService.logout()
        }
      } else {
        console.log('認証情報なし - 未ログイン状態')
      }

      setIsLoading(false)
      console.log('🏁 AuthContext 初期化完了')
    }

    initializeAuth()
  }, [fetchAccessibleFeatures])

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      setIsLoading(true)
      console.log('ログイン試行:', { email, timestamp: new Date().toISOString() })

      const response = await authService.login({ email, password })

      // MFAが必要な場合
      if (authService.isMfaRequired(response)) {
        const mfaResponse = response as MfaLoginResponse
        console.log('🔐 MFA認証が必要です')
        setIsLoading(false)
        return {
          requiresMfa: true,
          mfaSessionToken: mfaResponse.mfaSessionToken,
          message: mfaResponse.message,
        }
      }

      console.log('✅ API ログイン成功:', {
        user: response.user.name,
        role: response.user.role,
        requiresPasswordChange: response.requiresPasswordChange,
      })

      // トークンとユーザー情報をステートとローカルストレージに保存
      if (response.token) {
        setToken(response.token)
        setUser(response.user)
        localStorage.setItem('authToken', response.token)
        localStorage.setItem('user', JSON.stringify(response.user))
        console.log('💾 ローカルストレージに保存完了')

        // アクセス可能な機能を取得
        await fetchAccessibleFeatures(response.user.id)

        // ユーザーの言語設定に合わせてi18nを同期
        const userLanguage = mapLanguageToI18n(response.user.preferredLanguage)
        if (i18n.language !== userLanguage) {
          await i18n.changeLanguage(userLanguage)
          console.log('🌐 言語設定を同期:', userLanguage)
        }
      }

      // パスワード変更フラグを返却
      return {
        requiresPasswordChange: response.requiresPasswordChange,
        firstLogin: response.firstLogin,
      }
    } catch (error) {
      console.error('API ログイン失敗:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const registerNewTenant = async (data: NewTenantRegistrationData) => {
    setIsLoading(true)
    try {
      console.log('🔄 新規テナント登録開始:', data)
      const response = await authService.registerNewTenant(data)
      console.log('✅ 新規テナント登録成功:', response)

      // 登録成功時は自動でログイン状態にしない（初期パスワードでのログインが必要）
      // setToken と setUser は呼ばない

      return response
    } catch (error) {
      console.error('新規テナント登録失敗:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true)
      await authService.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setToken(null)
      setUser(null)
      setAccessibleFeatures([])
      setIsLoading(false)
    }
  }

  // 言語設定更新
  const updateLanguage = async (language: Language): Promise<void> => {
    if (!user) {
      throw new Error('ログインが必要です')
    }

    try {
      // バックエンドに言語設定を保存
      await authService.updateUserLanguage(user.id, language)

      // ユーザー情報を更新
      const updatedUser = { ...user, preferredLanguage: language }
      setUser(updatedUser)
      localStorage.setItem('user', JSON.stringify(updatedUser))

      // i18nの言語を変更
      const i18nLang = mapLanguageToI18n(language)
      await i18n.changeLanguage(i18nLang)

      console.log('🌐 言語設定を更新:', language)
    } catch (error) {
      console.error('言語設定更新失敗:', error)
      throw error
    }
  }

  const isAuthenticated = !!token && !!user

  // デバッグ情報をコンソールに出力（状態変更時のみ）
  React.useEffect(() => {
    console.log('🔍 AuthContext 状態変更:', {
      isAuthenticated,
      isLoading,
      hasToken: !!token,
      hasUser: !!user,
      userRole: user?.role,
    })
  }, [isAuthenticated, isLoading, token, user])

  const contextValue: AuthContextType = {
    user,
    token,
    login,
    registerNewTenant,
    logout,
    isAuthenticated,
    isLoading,
    setUser,
    setToken,
    updateLanguage,
    accessibleFeatures,
    canAccessRoute,
    refreshAccessibleFeatures,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthContext }
